import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import RedeemIcon from "@mui/icons-material/Redeem";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import MuiPhoneNumber from "mui-phone-number";
import PropTypes from "prop-types";
import React, { memo, useMemo, useState } from "react";
import { Controller } from "react-hook-form-v7";
import ColorPickDialog from "components/complex/dialogs/color-picker";
import { filterChangedColumnOptions } from "utils/conditions/changedColumnOptions";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useTheme } from "@mui/material/styles";
import getStyles from "../style";
import useDeviceType from "../../../../hooks/common/useDeviceType";
import { useSelector } from "react-redux";
import { userDataSelector } from "store/user/selectors";
import { isEqual } from "lodash";
import { formatPhoneNumberByCountry } from "utils/helpers";

const Field = memo(
  ({
    control,
    type,
    name,
    options,
    defaultValue,
    className,
    // dirty,
    error,
    fullWidth,
    InputLabelProps,
    // hidden,
    customOnFocus,
    customOnPaste,
    isItemColoured,
    isBISource,
    ...otherProps
  }) => {
    const { isMobile } = useDeviceType();
    const theme = useTheme();
    const styles = getStyles(theme, { isMobile, needsLeftPadding: name === "country" });
    const [isColorPickerDialogOpen, setIsColorPickerDialogOpen] = useState(false);
    const { themeMode } = useSelector(userDataSelector, isEqual);

    // const defaultPhoneNumberCountry = () => {
    //   console.log(otherProps?.getValues(name), otherProps?.getValues("country"), 'name & country')
    //   if (!otherProps?.getValues(name) || otherProps?.getValues(name) === "" || otherProps?.getValues(name) === "+") {
    //     return otherProps?.getValues("country")?.toLowerCase() || "us";
    //   }
    // need to check country code based on the number
    //   return  "us";
    // };

    const defaultProps = useMemo(
      () => ({
        type,
        variant: otherProps.variant,
        label: otherProps?.label || name,
        error: Boolean(error),
        helperText: <span>{error || ""}</span>,
        FormHelperTextProps: {
          sx: styles.errorText
        },
        labelPlacement: otherProps?.labelPlacement || "end",
        needsHelper: otherProps?.needsHelper ?? true,
        margin: "normal",
        size: "small",
        sx: {
          ...styles.field,
          "& .MuiInputBase-input": styles.input
        },
        ...(isBISource ? { className: "bi-source-field" } : {}),
        inputProps: {
          autoComplete: "new-password",
          ...(otherProps?.onBlur && { onBlur: otherProps.onBlur })
        },
        InputLabelProps: {
          sx: {
            ...styles.label,
            "&.Mui-focused": styles.labelFocused,
            "&.MuiInputLabel-shrink": styles.labelShrink
          },
          ...((type === "date" || type === "datetime-local" || type === "phone") && { shrink: true }),
          ...(InputLabelProps && { ...InputLabelProps })
        },
        fullWidth,
        ...otherProps
      }),
      [error, otherProps, InputLabelProps]
    );

    switch (type) {
      case "select-multiple": {
        const ITEM_HEIGHT = 48;
        const ITEM_PADDING_TOP = 8;
        const selectMenuProps = {
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 250
            }
          },
          variant: "menu",
          // getContentAnchorEl: null,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left"
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left"
          }
        };
        const { InputLabelProps } = defaultProps;

        return (
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            render={({ field }) => {
              const handleOnChange = (e) => {
                if (
                  e.target.value.find((item) => item.code === "all") &&
                  e.target.value.length === options.length + 1
                ) {
                  field.onChange([]);
                  return;
                }
                if (e.target.value.find((item) => item.code === "all")) {
                  field.onChange(options);
                  return;
                }
                field.onChange(e.target.value);
              };
              return (
                <FormControl className={className} style={{ width: "100%" }}>
                  <InputLabel id={`${name}-label`} {...InputLabelProps}>
                    {defaultProps.label}
                  </InputLabel>
                  <Select
                    error={Boolean(defaultProps.error)}
                    {...field}
                    onChange={handleOnChange}
                    value={
                      Array.isArray(field.value)
                        ? options.filter((option) => field?.value?.some((v) => v.code === option.code))
                        : []
                    }
                    labelId={`${name}-label`}
                    multiple
                    variant="standard"
                    input={
                      <OutlinedInput
                        label={defaultProps.label}
                        size={defaultProps.size}
                        margin="dense"
                        {...defaultProps}
                      />
                    }
                    displayEmpty
                    MenuProps={selectMenuProps}
                    renderValue={(selected) => {
                      if (selected?.length && options?.length && selected?.length === options?.length) return "All";
                      return selected.map((v) => v.name).join(", ");
                    }}>
                    {!defaultProps.withoutAll && (
                      <MenuItem value={{ code: "all", name: "ALL" }} dense>
                        <Checkbox
                          checked={Array.isArray(field.value) ? field?.value?.length === options.length : false}
                          color="primary"
                          size={defaultProps.size}
                        />
                        <ListItemText primary="All" />
                      </MenuItem>
                    )}
                    {options?.map((option) => (
                      <MenuItem
                        key={option.code}
                        value={option}
                        dense
                        disabled={option.disabled}>
                        <Checkbox
                          checked={
                            Array.isArray(field.value) ? !!field.value.find((v) => v.code === option.code) : false
                          }
                          color="primary"
                          size={defaultProps.size}
                          disabled={option.disabled}
                        />
                        <ListItemText primary={option.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={styles.errorText} error={defaultProps.error}>
                    {defaultProps.helperText}
                  </FormHelperText>
                </FormControl>
              );
            }}
          />
        );
      }
      case "multi-select-enabled": {
        const ITEM_HEIGHT = 48;
        const ITEM_PADDING_TOP = 8;
        const selectMenuProps = {
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 250
            }
          },
          variant: "menu",
          // getContentAnchorEl: null,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left"
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left"
          }
        };
        const { InputLabelProps } = defaultProps;
        const filtered = options.filter((item) => !item.disabled);

        return (
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            render={({ field }) => {
              const handleOnChange = (e) => {
                if (
                  e.target.value.find((item) => item.code === "all") &&
                  e.target.value.length === filtered.length + 1
                ) {
                  field.onChange([]);
                  return;
                }
                if (e.target.value.find((item) => item.code === "all")) {
                  field.onChange(filtered);
                  return;
                }
                field.onChange(e.target.value);
              };
              return (
                <FormControl className={className} style={{ width: "100%" }}>
                  <InputLabel id={`${name}-label`} {...InputLabelProps}>
                    {defaultProps.label}
                  </InputLabel>
                  <Select
                    error={Boolean(defaultProps.error)}
                    {...field}
                    onChange={handleOnChange}
                    value={
                      Array.isArray(field.value)
                        ? options.filter((option) => field?.value?.some((v) => v.code === option.code))
                        : []
                    }
                    labelId={`${name}-label`}
                    multiple
                    variant="standard"
                    input={
                      <OutlinedInput
                        label={defaultProps.label}
                        size={defaultProps.size}
                        margin="dense"
                        {...defaultProps}
                      />
                    }
                    displayEmpty
                    MenuProps={selectMenuProps}
                    renderValue={(selected) => {
                      if (filtered.length === 0) return "";
                      if (selected.length === filtered.length) return "All";
                      return selected.map((v) => v.name).join(", ");
                    }}>
                    <MenuItem
                      value={{ code: "all", name: "ALL" }}
                      dense
                      disabled={!filtered.length}>
                      <Checkbox
                        checked={
                          Array.isArray(field.value) && filtered.length
                            ? field?.value?.length === filtered.length
                            : false
                        }
                        color="primary"
                        size={defaultProps.size}
                      />
                      <ListItemText primary="All" />
                    </MenuItem>
                    {options.map((option) => (
                      <MenuItem
                        key={option.code}
                        value={option}
                        dense
                        disabled={option.disabled}>
                        <Checkbox
                          checked={
                            Array.isArray(field.value) ? !!field.value.find((v) => v.code === option.code) : false
                          }
                          color="primary"
                          size={defaultProps.size}
                          disabled={option.disabled}
                        />
                        <ListItemText primary={option.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={styles.errorText} error={defaultProps.error}>
                    {defaultProps.helperText}
                  </FormHelperText>
                </FormControl>
              );
            }}
          />
        );
      }
      case "multi-select-accordion": {
        const ITEM_HEIGHT = 48;
        const ITEM_PADDING_TOP = 8;
        const selectMenuProps = {
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 250
            }
          },
          variant: "menu",
          // getContentAnchorEl: null,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left"
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left"
          }
        };
        const { InputLabelProps } = defaultProps;

        return (
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            render={({ field }) => {
              const removeDuplicates = (arr) =>
                arr.reduce((acc, current) => {
                  if (acc.find((item) => item === current)) {
                    return acc;
                  }
                  acc.push(current);
                  return acc;
                }, []);

              const onItemSelectionToggle = (event, id, value) => {
                const parent = options.find((option) => option.id === id);
                if (value) {
                  if (parent) {
                    const newSelected = removeDuplicates([
                      ...field?.value,
                      id,
                      ...parent.children.map((item) => item.id)
                    ]);

                    return field.onChange(newSelected);
                  }
                  const newSelected = removeDuplicates([...field?.value, id]);

                  return field.onChange(newSelected);
                }

                if (parent) {
                  const childIds = parent.children.map((child) => child.id);
                  const idsToRemove = new Set([parent.id, ...childIds]);

                  const filteredSelected = field?.value.filter((selectedId) => !idsToRemove.has(selectedId));

                  return field.onChange(filteredSelected);
                }

                const parentOfChild = options.find((option) => option.children.some((child) => child.id === id));

                const remainingChildrenSelected = field?.value.some(
                  (selected) => selected !== id && parentOfChild.children.some((child) => child.id === selected)
                );

                const filteredSelected = field?.value.filter((selected) => {
                  if (selected === id) return false;

                  // remove parent ONLY if no children remain selected
                  if (selected === parentOfChild.id && !remainingChildrenSelected) return false;

                  return true;
                });

                return field.onChange(filteredSelected);
              };

              return (
                <FormControl className={className} style={{ width: "100%" }}>
                  <InputLabel id={`${name}-label`} {...InputLabelProps}>
                    {defaultProps.label}
                  </InputLabel>
                  <Select
                    error={Boolean(defaultProps.error)}
                    {...field}
                    onChange={() => {}}
                    value={Array.isArray(field.value) ? field?.value?.map((item) => (item.id ? item.id : item)) : []}
                    labelId={`${name}-label`}
                    multiple
                    variant="standard"
                    input={
                      <OutlinedInput
                        label={defaultProps.label}
                        size={defaultProps.size}
                        margin="dense"
                        {...defaultProps}
                      />
                    }
                    displayEmpty
                    MenuProps={selectMenuProps}
                    renderValue={() =>
                      field?.value
                        ?.map((selected) => {
                          if (selected.name) {
                            return selected.name;
                          }
                          return options.reduce((acc, current) => {
                            if (selected === current.id) {
                              return current.id;
                            }
                            const foundChild = current.children.find((child) => child.id === selected);
                            if (foundChild) {
                              return foundChild.name;
                            }
                            return acc;
                          }, "");
                        })
                        .join(", ") || ""
                    }>
                    <RichTreeView
                      selectedItems={field?.value?.map((item) => (item.id ? item.id : item)) || []}
                      onItemSelectionToggle={onItemSelectionToggle}
                      multiSelect
                      checkboxSelection
                      items={options}
                    />
                  </Select>
                  <FormHelperText sx={styles.errorText} error={defaultProps.error}>
                    {defaultProps.helperText}
                  </FormHelperText>
                </FormControl>
              );
            }}
          />
        );
      }
      case "checkbox":
      case "switch": {
        return (
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue ?? false}
            render={({ field: { ref, value, onChange, onBlur, name: fieldName } }) => {
              const InputComponent = type === "checkbox" ? Checkbox : Switch;
              return (
                <FormControl sx={styles.field}>
                  <FormControlLabel
                    control={
                      <InputComponent
                        inputRef={ref}
                        name={fieldName}
                        checked={Boolean(value)}
                        onBlur={onBlur}
                        onChange={(e) => onChange(e.target.checked)}
                        size={defaultProps.size}
                        color="primary"
                        disabled={defaultProps.disabled}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        noWrap
                        style={{ opacity: defaultProps.disabled ? 0.5 : 1 }}>
                        {defaultProps.label}
                      </Typography>
                    }
                    labelPlacement={defaultProps.labelPlacement}
                  />
                  {defaultProps.needsHelper && (
                    <FormHelperText sx={styles.errorText} error={defaultProps.error}>
                      {defaultProps.helperText}
                    </FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
        );
      }
      case "radio": {
        return (
          <FormControl component="fieldset" sx={styles.field}>
            <Typography component="legend" variant="body1" color={defaultProps.disabled ? "textSecondary" : "textPrimary"}>
              {defaultProps.label}
            </Typography>
            <Controller
              control={control}
              name={name}
              defaultValue={defaultValue}
              render={({ field }) => (
                <RadioGroup row aria-label={name} {...field} {...otherProps}>
                  {options &&
                    options.map((option) => (
                      <FormControlLabel
                        key={option.code || option}
                        control={<Radio color="primary" size={defaultProps.size} />}
                        label={
                          <Typography
                            variant="body2"
                            color="textPrimary"
                            noWrap
                            style={{ opacity: otherProps?.disabled ? 0.5 : 1 }}>
                            {option.name || option}
                          </Typography>
                        }
                        value={option.code || option}
                        disabled={defaultProps.disabled}
                      />
                    ))}
                </RadioGroup>
              )}
            />
            <FormHelperText sx={styles.errorText} error={defaultProps.error}>
              {defaultProps.helperText}
            </FormHelperText>
          </FormControl>
        );
      }
      case "custom-date": {
        return (
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            render={({ field: { ref, value, onChange, ...restFieldProps } }) => (
              <DatePicker
                disablePast={defaultProps.disablePast}
                label={defaultProps.label}
                shouldDisableDate={defaultProps.shouldDisableDate}
                openTo="day"
                disabled={defaultProps.disabled}
                value={dayjs(value || defaultValue)}
                // InputAdornmentProps={{ position: "start" }}
                onChange={(newValue) => {
                  onChange(newValue);
                }}
                slotProps={{
                  textField: {
                    ...defaultProps,
                    size: "small",
                    // error: false,
                    sx: {
                      ...styles.field,
                      width: "100%"
                    }
                  }
                }}
              />
            )}
          />
        );
      }
      case "custom-date-time": {
        return (
          <Controller
            control={control}
            name={name}
            defaultValue={dayjs(defaultValue)}
            render={({ field: { ref, value, onChange, ...restFieldProps } }) => (
              <DateTimePicker
                disabled={otherProps?.disabled}
                label={defaultProps.label}
                disablePast
                value={dayjs(value || defaultValue || null)}
                onChange={(newValue) => onChange(newValue)}
                slotProps={{
                  textField: {
                    ...defaultProps,
                    sx: {
                      ...styles.field,
                      width: "100%"
                    },
                    size: "small"
                  }
                }}
              />
            )}
          />
        );
      }

      case "select-searchable": {
        return (
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            render={({ field: { ref, value, onChange, ...fieldRestProps } }) => {
              // Prefer optionKey so same-named tokens in different groups
              // (e.g. Document Status vs Opportunity Status) stay distinct.
              const selectedOption =
                options && value
                  ? (options.find((o) => o.optionKey === value) ||
                      (options.filter((o) => o.code === value).length === 1
                        ? options.find((o) => o.code === value)
                        : null) ||
                      null)
                  : null;

              return (
                <Autocomplete
                  {...fieldRestProps}
                  disabled={defaultProps.disabled}
                  options={options || []}
                  value={selectedOption}
                  groupBy={(o) => o.group}
                  getOptionLabel={(o) => o?.name || ""}
                  isOptionEqualToValue={(o, v) =>
                    o.optionKey && v?.optionKey ? o.optionKey === v.optionKey : o.code === v?.code
                  }
                  filterOptions={(opts, state) =>
                    filterChangedColumnOptions(opts, state.inputValue)
                  }
                  onChange={(_, newValue) => {
                    onChange(newValue ? newValue.optionKey || newValue.code : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={ref}
                      label={defaultProps.label}
                      error={Boolean(defaultProps.error)}
                      helperText={<span>{defaultProps.error || ""}</span>}
                      FormHelperTextProps={{ sx: styles.errorText }}
                      size="small"
                      margin="normal"
                      variant="outlined"
                      sx={{ ...styles.field, "& .MuiInputBase-input": styles.input }}
                      InputLabelProps={{
                        sx: {
                          ...styles.label,
                          "&.Mui-focused": styles.labelFocused,
                          "&.MuiInputLabel-shrink": styles.labelShrink
                        }
                      }}
                    />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Box
                        component="div"
                        sx={{
                          position: "sticky",
                          top: "-8px",
                          padding: "4px 10px",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          backgroundColor: "background.paper",
                          zIndex: 1
                        }}>
                        {params.group}
                      </Box>
                      <ul style={{ padding: 0 }}>{params.children}</ul>
                    </li>
                  )}
                />
              );
            }}
          />
        );
      }

      // other input type
      default: {
        return (
          <Controller
            control={control}
            name={name}
            defaultValue={defaultValue}
            render={({ field: { ref, ...fieldRestProps } }) => {
              switch (type) {
                case "select": {
                  return (
                    <TextField native={name !== "country"} inputRef={ref} {...fieldRestProps} {...defaultProps} select>
                      {options &&
                        options.map((option, i) => {
                          if (option.code === "divider") {
                            return <Divider key={option.code + i} />;
                          }

                          if (option.code === "subHeader") {
                            return (
                              <ListSubheader key={`subheader-${option.subHeader}-${i}`} disableSticky>
                                {option.subHeader}
                              </ListSubheader>
                            );
                          }

                          const showFlag = name === "country" && option.code;
                          const flagSrc = showFlag ? `/assets/flags/${option.code}.png` : null;
                          const isBundleChild = Boolean(option.is_bundle_child);
                          return (
                            <MenuItem
                              key={option.code || option}
                              value={option.code !== undefined ? option.code : option}
                              disabled={option.code === "disabled" || option.disabled}
                              onClick={option.onClick}
                              sx={
                                isBundleChild
                                  ? {
                                      pl: 4,
                                      color: "text.secondary",
                                      fontSize: "0.875rem"
                                    }
                                  : undefined
                              }
                              style={{
                                position: "relative",
                                paddingRight: isItemColoured ? "28px" : undefined,
                                paddingLeft: name === "country" ? "36px" : undefined
                              }}>
                              {flagSrc && (
                                <img
                                  src={flagSrc}
                                  alt={option.name}
                                  style={{
                                    width: "20px",
                                    height: "15px",
                                    objectFit: "cover",
                                    position: "absolute",
                                    left: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)"
                                  }}
                                />
                              )}
                              {isBundleChild && (
                                <RedeemIcon
                                  sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                                  color="primary"
                                />
                              )}
                              {option.name || option}
                              {isItemColoured && (
                                <Box
                                  className={"color-box"}
                                  sx={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "20px",
                                    height: "15px",
                                    borderRadius: "3px",
                                    backgroundColor: `#${option.color}`
                                  }}
                                />
                              )}
                            </MenuItem>
                          );
                        })}
                    </TextField>
                  );
                }
                case "textarea": {
                  return <TextField inputRef={ref} {...fieldRestProps} multiline rows={4} {...defaultProps} />;
                }
                case "hidden": {
                  return <input ref={ref} {...fieldRestProps} hidden />;
                }
                case "phone": {
                  const phoneValue = fieldRestProps.value ?? "";
                  // const country = otherProps?.getValues?.("country")?.toLowerCase() || "us";

                  const formattedPhoneValue =
                    phoneValue && phoneValue !== "+"
                      ? formatPhoneNumberByCountry(phoneValue)
                      : phoneValue;

                  return (
                    <div>
                      <MuiPhoneNumber
                        classes={"phonee"}
                        ref={ref}
                        {...fieldRestProps}
                        value={formattedPhoneValue}
                        {...defaultProps}
                        sx={{
                          ...defaultProps.sx,
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: `1px solid ${themeMode ? "rgb(179 179 179)" : "rgba(255, 255, 255, 0.3)"} !important`
                          },
                          "& .MuiOutlinedInput-input.MuiInputBase-input": {
                            color: `${themeMode ? "inherit" : theme.palette.text.primary} !important`,
                            WebkitTextFillColor: `${themeMode ? "inherit" : theme.palette.text.primary} !important`
                          },
                          "& .MuiInputBase-root.Mui-disabled .MuiInputLabel-outlined": {
                            color: `${themeMode ? "rgba(0, 0, 0, 0.38)" : "rgba(255, 255, 255, 0.5)"} !important`
                          },
                          "& .MuiOutlinedInput-input.MuiInputBase-input.Mui-disabled": {
                            color: `${themeMode ? "rgba(0, 0, 0, 0.38)" : "rgba(255, 255, 255, 0.5)"} !important`,
                            WebkitTextFillColor: `${themeMode ? "rgba(0, 0, 0, 0.38)" : "rgba(255, 255, 255, 0.5)"} !important`
                          }
                        }}
                        defaultCountry="us"
                        disableDropdown={defaultProps.disabled}
                        onlyCountries={["us"]}
                      />
                    </div>
                  );
                }
                case "color-picker": {
                  return (
                    <>
                      <Button sx={styles.colorPicker} onClick={() => setIsColorPickerDialogOpen(true)}>
                        <Box sx={{ backgroundColor: fieldRestProps.value }} />

                        {defaultProps.label}
                      </Button>
                      <ColorPickDialog
                        // {...fieldRestProps}
                        color={fieldRestProps.value}
                        closePopup={() => setIsColorPickerDialogOpen(undefined)}
                        isOpen={isColorPickerDialogOpen}
                        handleChange={(color) => fieldRestProps.onChange(color.hex)}
                      />
                    </>
                  );
                }
                default: {
                  const hasValue =
                    fieldRestProps.value !== undefined &&
                    fieldRestProps.value !== null &&
                    fieldRestProps.value !== "";
                  return (
                    <TextField
                      {...(customOnFocus && { onFocus: customOnFocus })}
                      {...(customOnPaste && { onPaste: customOnPaste })}
                      inputRef={ref}
                      {...fieldRestProps}
                      {...defaultProps}
                      InputLabelProps={{
                        ...defaultProps.InputLabelProps,
                        ...(hasValue ? { shrink: true } : {})
                      }}
                    />
                  );
                }
              }
            }}
          />
        );
      }
    }
  }
);

Field.propTypes = {
  control: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  error: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.number.isRequired,
      PropTypes.shape({
        name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        code: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
      }).isRequired
    ])
  ),
  variant: PropTypes.string,
  fullWidth: PropTypes.bool
};

Field.defaultProps = {
  control: undefined,
  defaultValue: undefined,
  error: undefined,
  options: undefined,
  variant: "outlined",
  fullWidth: true
};

export default Field;
